import { ui } from "@patient-watch/ui";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Search, X } from "lucide-react";
import { useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import {
    type AdminEntityKind,
    type AdminEntityOption,
    type AdminEntityScope,
    isUuid,
    listsWithoutQuery,
    resolveAdminEntity,
    searchAdminEntity,
} from "./adminEntitySearch";
import { useDebouncedValue } from "./useDebouncedValue";

const ENTITY_COPY: Record<AdminEntityKind, { placeholder: string; search: string; hint: string; noun: string }> = {
    organisation: {
        placeholder: "All organisations",
        search: "Search name or paste UUID",
        hint: "",
        noun: "organisation",
    },
    form: {
        placeholder: "All questionnaires",
        search: "Search name or paste UUID",
        hint: "",
        noun: "questionnaire",
    },
    input: {
        placeholder: "All inputs",
        search: "Search input label or paste UUID",
        hint: "Type an input label, or paste an input UUID.",
        noun: "input",
    },
    diary: {
        placeholder: "All diaries",
        search: "Search patient, hospital number or UUID",
        hint: "Type a patient name or hospital number, or paste a diary UUID.",
        noun: "diary",
    },
    user: {
        placeholder: "All users",
        search: "Search name, email or paste UUID",
        hint: "Type a name or email, or paste a user UUID.",
        noun: "user",
    },
    entry: {
        placeholder: "All entries",
        search: "Search answers or input labels, or paste UUID",
        hint: "Type an answer or an input label, such as Pain Score, or paste an entry UUID.",
        noun: "entry",
    },
};

const resolveKey = (entity: AdminEntityKind, id: string | null) => ["admin-entity-resolve", entity, id] as const;

/** Name of an entity picked in an admin filter, for "Filtered by" chips. Falls back to a short id. */
export function AdminEntityName({ entity, id }: { entity: AdminEntityKind; id: string }) {
    const { data } = useQuery({
        queryKey: resolveKey(entity, id),
        queryFn: () => resolveAdminEntity(entity, id),
        staleTime: 1000 * 60 * 5,
    });
    return <>{data?.label ?? shortId(id)}</>;
}

function shortId(id: string) {
    return id.slice(0, 8);
}

interface Props {
    entity: AdminEntityKind;
    value: string | null;
    onChange: (value: string | null) => void;
    scope?: AdminEntityScope;
    id?: string;
}

const cardClass =
    "flex w-full items-start justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left dark:border-slate-700 dark:bg-slate-900";

function OptionCard({
    title,
    description,
    entityId,
    onSelect,
}: {
    title: string;
    description?: string | null;
    entityId: string;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            // Keep focus inside the picker so the result list stays open until the click lands.
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSelect}
            className={cn(
                cardClass,
                "hover:bg-indigo-50 hover:border-indigo-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 dark:hover:bg-indigo-950"
            )}
        >
            <span className="space-y-0.5 min-w-0">
                <span className="block font-medium text-slate-900 dark:text-slate-100 text-sm truncate">{title}</span>
                {description ? (
                    <span className="block text-muted-foreground text-xs truncate">{description}</span>
                ) : null}
            </span>
            <span className="pt-0.5 font-mono text-2xs text-muted-foreground shrink-0">{shortId(entityId)}</span>
        </button>
    );
}

/**
 * Admin search field: type a name (or paste a UUID) and pick a result card. Once picked,
 * the field shows the entity as a card with its full id and a button to change it.
 */
export function AdminEntityPicker({ entity, value, onChange, scope = {}, id }: Props) {
    const copy = ENTITY_COPY[entity];
    const queryClient = useQueryClient();
    const inputRef = useRef<HTMLInputElement>(null);
    const [active, setActive] = useState(false);
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query.trim(), 250);
    const pastedId = isUuid(query.trim()) ? query.trim().toLowerCase() : null;

    const searchEnabled = active && (debouncedQuery.length > 0 || listsWithoutQuery(entity, scope));

    const { data: results = [], isFetching } = useQuery({
        queryKey: ["admin-entity-search", entity, debouncedQuery, scope.formId ?? null],
        queryFn: () => searchAdminEntity(entity, debouncedQuery, scope),
        enabled: searchEnabled,
        staleTime: 1000 * 60,
        placeholderData: (previous) => previous,
    });

    const { data: selected, isLoading: isResolving } = useQuery({
        queryKey: resolveKey(entity, value),
        queryFn: () => resolveAdminEntity(entity, value as string),
        enabled: Boolean(value),
        staleTime: 1000 * 60 * 5,
    });

    const select = (option: AdminEntityOption | null, rawId?: string) => {
        if (option) queryClient.setQueryData(resolveKey(entity, option.id), option);
        onChange(option?.id ?? rawId ?? null);
        setActive(false);
        setQuery("");
    };

    const change = () => {
        onChange(null);
        setActive(true);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const onBlur = (e: FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActive(false);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setQuery("");
            setActive(false);
        }
        if (e.key === "Enter") {
            e.preventDefault();
            if (pastedId && !results.some((r) => r.id === pastedId)) select(null, pastedId);
            else if (results[0]) select(results[0]);
        }
    };

    if (value) {
        const title = selected?.label ?? (isResolving ? "Loading…" : `Unknown ${copy.noun}`);
        return (
            <div className={cn(cardClass, "items-center border-indigo-200 bg-indigo-50/60 dark:bg-indigo-950/40")}>
                <Check className="size-4 text-indigo-600 shrink-0" />
                <div className="flex-1 space-y-0.5 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">{title}</p>
                    {selected?.description ? (
                        <p className="text-muted-foreground text-xs truncate">{selected.description}</p>
                    ) : null}
                    <p className="font-mono text-2xs text-muted-foreground truncate">{value}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <ui.Button id={id} type="button" variant="ghost" size="sm" onClick={change}>
                        Change
                    </ui.Button>
                    <ui.Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Clear ${copy.noun}`}
                        onClick={() => onChange(null)}
                    >
                        <X className="size-4" />
                    </ui.Button>
                </div>
            </div>
        );
    }

    const showPastedIdCard = Boolean(pastedId) && !results.some((r) => r.id === pastedId);
    const showHint = !query.trim() && !listsWithoutQuery(entity, scope);
    const showEmpty = !showHint && !isFetching && !showPastedIdCard && results.length === 0 && query.trim();

    return (
        <div className="space-y-2" onBlur={onBlur}>
            <div className="relative">
                <Search className="top-1/2 left-3 absolute size-4 text-muted-foreground -translate-y-1/2 pointer-events-none" />
                <ui.Input
                    ref={inputRef}
                    id={id}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(true);
                    }}
                    onFocus={() => setActive(true)}
                    onKeyDown={onKeyDown}
                    placeholder={copy.search}
                    autoComplete="off"
                    className="pr-9 pl-9 w-full h-12"
                />
                {isFetching ? (
                    <Loader2 className="top-1/2 right-3 absolute size-4 text-muted-foreground -translate-y-1/2 animate-spin" />
                ) : null}
            </div>

            {active ? (
                <div className="space-y-1.5 pr-1 max-h-72 overflow-y-auto">
                    {showHint ? <p className="px-0.5 text-muted-foreground text-xs">{copy.hint}</p> : null}
                    {showPastedIdCard && pastedId ? (
                        <OptionCard
                            title="Use this ID"
                            description={pastedId}
                            entityId={pastedId}
                            onSelect={() => select(null, pastedId)}
                        />
                    ) : null}
                    {results.map((option) => (
                        <OptionCard
                            key={option.id}
                            title={option.label}
                            description={option.description}
                            entityId={option.id}
                            onSelect={() => select(option)}
                        />
                    ))}
                    {showEmpty ? (
                        <p className="px-0.5 text-muted-foreground text-xs">{`No ${copy.noun} found.`}</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
